import { create } from 'zustand';
import type { Note, NoteListItem } from '@/lib/api';

interface NotesState {
  notes: NoteListItem[];
  activeNote: Note | null;
  isDirty: boolean;
}

interface NotesActions {
  setNotes: (notes: NoteListItem[]) => void;
  upsertNoteListItem: (note: NoteListItem) => void;
  removeNoteListItem: (id: string) => void;

  setActiveNote: (note: Note | null) => void;
  updateActiveNote: (patch: Partial<Note>) => void;

  markDirty: () => void;
  markClean: () => void;

  reset: () => void;
}

type NotesStore = NotesState & NotesActions;

export const useNotesStore = create<NotesStore>()((set) => ({
  notes: [],
  activeNote: null,
  isDirty: false,

  setNotes: (notes) => set({ notes }),

  upsertNoteListItem: (note) =>
    set((s) => {
      const idx = s.notes.findIndex((n) => n.id === note.id);
      const next = [...s.notes];
      if (idx >= 0) {
        next[idx] = note;
      } else {
        next.unshift(note);
      }
      next.sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      return { notes: next };
    }),

  removeNoteListItem: (id) =>
    set((s) => ({
      notes: s.notes.filter((n) => n.id !== id),
      activeNote: s.activeNote?.id === id ? null : s.activeNote,
    })),

  setActiveNote: (note) => set({ activeNote: note, isDirty: false }),

  updateActiveNote: (patch) =>
    set((s) => (s.activeNote ? { activeNote: { ...s.activeNote, ...patch } } : {})),

  markDirty: () => set({ isDirty: true }),
  markClean: () => set({ isDirty: false }),

  reset: () => set({ notes: [], activeNote: null, isDirty: false }),
}));
